/* ============================================================
   EXON FOLD (EXO-4667) — one folded row for every stacked element.
   window.ExonFold. Markup: snippets/fold-row.liquid. CSS: the xf-
   block at the end of assets/upsell.css. Contract: docs/exo-4667/
   PLAN.md §2 as amended by §8 (F6, F10).

   The row knows NOTHING about what it holds. An owner fills five
   slots through set() (icon, title, state, bar, status/tone) and
   the controller opens and folds the panel. Every call resolves
   from the DOM: no registry, so rows rendered later just work.

   STATE lives on the .xf node (data-xf-open, aria-expanded, inert)
   and, for the auto-open seen-flags only, in sessionStorage. Never
   inside an element's own mount: owners rebuild those freely.

   GROUP = the SURFACE (data-xf-group="drawer|popup|cart"): one row
   open per group. A row with data-xf-controlled is never opened or
   closed by the controller; a tap (or its group closing it) is a
   REQUEST, the cancelable `xf:toggle` event, and the owning island
   COMMITS with ExonFold.open/close. A .xf-row with data-xf-go
   dispatches `xf:go` and never opens.

   AUTO-OPEN IS NEWS, AND IT IS BRIEF (owner, 2026-10-05T16:39Z). An
   owner passes a token only when its row's state just changed; the
   row opens once per token, and a row that opened BY ITSELF folds
   again on the customer's next tap outside it or next scroll of its
   surface. A row the customer opened stays open until they close it.
   No timers.

   NEVER THROWS: owners call it from inside the gift claim's paint,
   so every public entry is wrapped and a failure is a no-op.
   ============================================================ */
(function () {
  'use strict';
  if (window.ExonFold) return;

  var SEEN_PREFIX = 'exon_xf_auto:';
  var memSeen = {};   // the seen-set when sessionStorage is unavailable

  var ICONS = {
    gift: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>',
    check: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>'
  };

  function q(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function attrEsc(s) { return String(s).replace(/["\\]/g, '\\$&'); }
  function isRow(x) { return !!(x && x.nodeType === 1 && x.hasAttribute && x.hasAttribute('data-xf')); }
  /* the row's own children, never a nested row's */
  function part(x, cls) {
    for (var c = x.firstElementChild; c; c = c.nextElementSibling) {
      if (c.classList && c.classList.contains(cls)) return c;
    }
    return null;
  }
  function btnOf(x) { return part(x, 'xf-row'); }
  function panelOf(x) { return part(x, 'xf-panel'); }
  function slot(x, name) { var b = btnOf(x); return b ? q('[' + name + ']', b) : null; }
  /* per-row memory of what set() last wrote: an expando, not a DOM write */
  function mem(x) { return x.__xf || (x.__xf = {}); }
  function groupOf(x) { return x.getAttribute('data-xf-group') || ''; }
  function controlled(x) { return x.hasAttribute('data-xf-controlled'); }
  function emit(x, type, detail, cancelable) {
    var ev;
    try { ev = new CustomEvent(type, { bubbles: true, cancelable: !!cancelable, detail: detail || {} }); }
    catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent(type, true, !!cancelable, detail || {}); }
    x.dispatchEvent(ev);
    return ev;
  }

  /* ---- open state: the three writes always move together ---- */
  function writeOpen(x, open) {
    var v = open ? 'true' : 'false';
    if (x.getAttribute('data-xf-open') !== v) x.setAttribute('data-xf-open', v);
    var b = btnOf(x);
    if (b && b.getAttribute('aria-expanded') !== v) b.setAttribute('aria-expanded', v);
    var p = panelOf(x);
    if (!p) return;
    /* the property where the engine has it, the attribute where it does not */
    if ('inert' in p) { if (p.inert !== !open) p.inert = !open; }
    else if (open) { if (p.hasAttribute('inert')) p.removeAttribute('inert'); }
    else if (!p.hasAttribute('inert')) p.setAttribute('inert', '');
  }
  /* The server renders every panel OPEN (the no-JS floor). The first time
     the controller touches a row it folds it, atomically, and stamps it,
     handing any focus inside the panel to the row first, as a close does. */
  function ready(x) {
    if (x.hasAttribute('data-xf-ready')) return;
    rescueFocus(x);
    writeOpen(x, false);
    x.setAttribute('data-xf-ready', '');
  }
  function isOpen(x) { return x.getAttribute('data-xf-open') === 'true'; }

  /* closing a panel that holds the focus would strand it behind inert */
  function rescueFocus(x) {
    var p = panelOf(x), a = document.activeElement;
    if (p && a && a !== document.body && p.contains(a)) {
      var b = btnOf(x);
      if (b && b.focus) b.focus();
    }
  }
  function commitClose(x) {
    disarm(x);
    if (!isOpen(x)) { writeOpen(x, false); return; }
    rescueFocus(x);
    writeOpen(x, false);
    emit(x, 'xf:close');
  }
  function commitOpen(x) {
    if (isOpen(x)) return;
    writeOpen(x, true);
    emit(x, 'xf:open');
  }

  /* ONE OPEN ROW PER GROUP. Every other open row of the group is asked to
     close, synchronously: an uncontrolled row is closed here; a controlled
     row gets the request and must have committed before dispatchEvent
     returns. True only if none is left open. */
  function closeOthers(x) {
    var g = groupOf(x);
    if (!g) return true;
    var clear = true;
    qa('[data-xf][data-xf-group="' + attrEsc(g) + '"][data-xf-open="true"]').forEach(function (o) {
      if (o === x) return;
      ready(o);
      if (controlled(o)) emit(o, 'xf:toggle', { want: false, reason: 'group' }, true);
      else commitClose(o);
      if (isOpen(o)) clear = false;
    });
    return clear;
  }
  function doOpen(x) {
    ready(x);
    if (isOpen(x)) return true;
    if (!closeOthers(x)) return false;
    commitOpen(x);
    return true;
  }

  /* ---- auto-open, once per session per group per key per token ---- */
  function seenKey(x, token) { return SEEN_PREFIX + groupOf(x) + ':' + (x.getAttribute('data-xf-key') || '') + ':' + token; }
  function seen(k) {
    if (memSeen[k]) return true;
    try { return sessionStorage.getItem(k) === '1'; } catch (e) { return false; }
  }
  function markSeen(k) {
    memSeen[k] = true;
    try { sessionStorage.setItem(k, '1'); } catch (e) {}
  }
  /* "first view" is the customer's first view: a row that is not rendered
     (its drawer or popup is closed, or the row is hidden) waits for the
     next set() after it is shown */
  function rendered(x) { return !x.hidden && x.getClientRects().length > 0; }
  function tryAuto(x) {
    var m = mem(x), tok = m.auto;
    if (tok == null || tok === '') return;
    var k = seenKey(x, tok);
    if (seen(k)) { m.auto = null; return; }
    if (!rendered(x)) return;
    /* a controlled row is the owner's to open; an open row has nothing to do */
    if (controlled(x)) return;
    var was = isOpen(x);
    if (!was && !doOpen(x)) return;   // a group member refused: token kept
    markSeen(k);
    m.auto = null;
    if (!was) arm(x);   // opened by itself: it folds on the next scroll or tap
  }

  /* ---- an auto-opened row folds on the customer's next move ----
     The move is a pointerdown OUTSIDE the row (a tap inside it, on the
     claim button, is the row's own business), or the customer scrolling
     the surface: the wheel, a touch drag or a scroll key on the
     surface's scroller (.cd-body in the drawer, .up-scroll in the popup,
     the page elsewhere). Scroll INPUT, not the scroll event: a layout
     change (the panel opening, scroll anchoring) moves a scroller
     without the customer, and must not fold the row it is opening.
     One-shot: every listener goes when it fires or the row closes. */
  var SCROLLERS = { drawer: ['.cart-drawer', '.cd-body'], popup: ['.up-modal', '.up-scroll'] };
  var SCROLL_KEYS = { PageDown: 1, PageUp: 1, ArrowDown: 1, ArrowUp: 1, Home: 1, End: 1, ' ': 1, Spacebar: 1 };
  var PAGE_KEYS = { PageDown: 1, PageUp: 1, Home: 1, End: 1 };   // scroll the page even from a control inside the row
  function scrollerOf(x) {
    var s = SCROLLERS[groupOf(x)];
    var root = s && x.closest ? x.closest(s[0]) : null;
    return (root && q(s[1], root)) || window;
  }
  function inside(x, t) { return !!(t && t.nodeType === 1 && x.contains(t)); }
  function editable(t) {
    return !!(t && t.nodeType === 1 && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)));
  }
  function disarm(x) {
    var m = x && x.__xf;
    if (!m || !m.off) return;
    var off = m.off;
    m.off = null;
    try { off(); } catch (e) {}   // a close never fails on its listeners
  }
  function arm(x) {
    disarm(x);
    var sc = scrollerOf(x);
    var opts = { capture: true, passive: true };
    function fold() {
      disarm(x);
      if (isOpen(x) && !controlled(x)) commitClose(x);
    }
    function onDown(e) { try { if (!inside(x, e.target)) fold(); } catch (err) {} }
    function onScroll() { try { fold(); } catch (err) {} }
    function onKey(e) {
      try {
        if (!SCROLL_KEYS[e.key] || (!PAGE_KEYS[e.key] && inside(x, e.target)) || editable(e.target)) return;
        fold();
      } catch (err) {}
    }
    var on = [[document, 'pointerdown', onDown], [document, 'keydown', onKey], [sc, 'wheel', onScroll], [sc, 'touchmove', onScroll]];
    mem(x).off = function () {
      on.forEach(function (l) { try { l[0].removeEventListener(l[1], l[2], opts); } catch (e) {} });
    };
    on.forEach(function (l) { try { l[0].addEventListener(l[1], l[2], opts); } catch (e) {} });
  }

  /* ---- slots ---- */
  function writeTitle(x, model) {
    var t = slot(x, 'data-xf-title');
    if (!t) return;
    var m = mem(x);
    if (model.titleHtml != null) {
      var h = String(model.titleHtml);
      if (m.title !== 'h:' + h) { t.innerHTML = h; m.title = 'h:' + h; }
    } else if (model.title != null) {
      var s = String(model.title);
      if (m.title !== 't:' + s) {
        if (t.textContent !== s) t.textContent = s;
        m.title = 't:' + s;
      }
    }
  }
  function writeIcon(x, icon) {
    var ic = slot(x, 'data-xf-ic');
    if (!ic) return;
    var m = mem(x), h = ICONS[icon] || String(icon);
    if (m.icon === h) return;
    ic.innerHTML = h;
    m.icon = h;
  }
  function writeState(x, st) {
    var s = slot(x, 'data-xf-state');
    if (!s) return;
    var m = mem(x);
    if (st == null) {
      if (!s.hidden) s.hidden = true;
      return;
    }
    var kind = st.kind === 'word' ? 'word' : 'num';
    if (s.getAttribute('data-xf-kind') !== kind) s.setAttribute('data-xf-kind', kind);
    var sig = st.html != null ? 'h:' + st.html : 't:' + (st.text == null ? '' : st.text);
    if (m.state !== sig) {
      if (st.html != null) s.innerHTML = String(st.html);
      else s.textContent = String(st.text == null ? '' : st.text);
      m.state = sig;
    }
    if (s.hidden) s.hidden = false;
  }
  function writeBar(x, bar) {
    var b = slot(x, 'data-xf-bar');
    if (!b) return;
    if (bar == null || !isFinite(Number(bar))) {
      if (!b.hidden) b.hidden = true;
      return;
    }
    var pct = Math.max(0, Math.min(100, Math.round(Number(bar)))) + '%';
    var i = b.firstElementChild;
    if (!i) { i = document.createElement('i'); b.appendChild(i); }
    if (i.style.width !== pct) i.style.width = pct;
    if (b.hidden) b.hidden = false;
  }
  function writeAttr(x, name, v) { if (x.getAttribute(name) !== v) x.setAttribute(name, v); }

  function set(x, model) {
    if (!isRow(x) || !model) return;
    ready(x);
    var prev = x.getAttribute('data-xf-status');
    if ('hidden' in model && x.hidden !== !!model.hidden) x.hidden = !!model.hidden;
    if ('title' in model || 'titleHtml' in model) writeTitle(x, model);
    if ('icon' in model && model.icon != null) writeIcon(x, model.icon);
    if ('state' in model) writeState(x, model.state);
    if ('bar' in model) writeBar(x, model.bar);
    if ('tone' in model && model.tone) writeAttr(x, 'data-xf-tone', model.tone === 'amber' ? 'amber' : 'teal');
    if ('status' in model && model.status) writeAttr(x, 'data-xf-status', String(model.status));
    /* the customer claimed it: the row reports it and folds itself, once
       (on the transition, so a later tap to look again is never undone) */
    if (prev === 'action' && x.getAttribute('data-xf-status') === 'done' && isOpen(x) && !controlled(x)) commitClose(x);
    if ('auto' in model) mem(x).auto = model.auto == null ? null : String(model.auto);
    /* every visible set with a pending token attempts the auto-open,
       whether or not anything else changed */
    tryAuto(x);
  }

  function rowFor(surface, key) {
    var s = attrEsc(surface), k = attrEsc(key);
    return q('[data-xf][data-xf-group="' + s + '"][data-xf-key="' + k + '"]') ||
      q('[data-xf-stack="' + s + '"] [data-xf][data-xf-key="' + k + '"]');
  }

  /* a tap: a lead-elsewhere row reports it; a controlled row asks its
     owner; any other row toggles. Enter and Space reach here as the
     button's own click — no key handlers, so nothing double-fires. */
  function onTap(x) {
    ready(x);
    var b = btnOf(x);
    if (b && b.hasAttribute('data-xf-go')) { emit(x, 'xf:go', { reason: 'tap' }); return; }
    if (controlled(x)) { emit(x, 'xf:toggle', { want: !isOpen(x), reason: 'tap' }, true); return; }
    if (isOpen(x)) commitClose(x); else doOpen(x);
  }

  function guard(fn, fallback) {
    return function () {
      try { return fn.apply(null, arguments); } catch (e) { return fallback; }
    };
  }

  window.ExonFold = {
    row: guard(function (surface, key) { return rowFor(surface, key); }, null),
    set: guard(set),
    /* owner COMMITS: never dispatch xf:toggle for the row itself */
    open: guard(function (x) { return isRow(x) ? doOpen(x) : false; }, false),
    close: guard(function (x) { if (isRow(x)) { ready(x); commitClose(x); } }),
    toggle: guard(function (x) { if (isRow(x)) onTap(x); }),
    isOpen: guard(function (x) { return isRow(x) && isOpen(x); }, false)
  };

  /* ---- boot: fold what the server rendered open, one delegated tap ---- */
  function boot() {
    qa('[data-xf]').forEach(function (x) { try { ready(x); } catch (e) {} });
    document.addEventListener('click', function (e) {
      try {
        var b = e.target && e.target.closest && e.target.closest('.xf-row');
        if (!b) return;
        var x = b.parentNode;
        if (!isRow(x) || btnOf(x) !== b) return;
        e.preventDefault();
        onTap(x);
      } catch (err) {}
    });
  }
  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  } catch (e) {}
})();
